import * as React from 'react'
import classNames from 'classnames'
import { Cell, DataRow, HeaderRow, InlineDataTable, TableHeader } from 'uiComponents/table'
import { TooltipWrapper } from 'uiComponents/tooltip.new/tooltipWrapper'
import { SkeletonBar } from 'uiComponents/skeleton/skeleton'
import { Reveal } from 'uiComponents/reveal/reveal'
import { TableSort } from 'uiComponents/table/tableSort'
import './productsTable.scss'
import { useTranslation } from 'translations'

export interface ProductsColumn {
    key: string
    header: string
    width?: string
    align?: 'left' | 'right'
    headerTooltip?: React.ReactNode
    sortable?: boolean
}

const ACTIONS_COLUMN_WIDTH = '3.5rem'
const SKELETON_ROWS = [0, 1, 2]
const CELL_CLASS = 'capacity-table-cell wrapContent overflow-allowed'
const FLIP_HOLD_CLASS = 'capacity-table-row-flip-hold'

interface ProductsRowProps {
    className?: string
    children: React.ReactNode
}

export const ProductsRow = React.forwardRef<HTMLDivElement, ProductsRowProps>(function ProductsRow(
    { className, children },
    ref,
) {
    return (
        <DataRow ref={ref} role="row" className={classNames('capacity-table-row', className)}>
            {children}
        </DataRow>
    )
})

interface ProductsCellProps {
    className?: string
    align?: 'left' | 'right'
    children?: React.ReactNode
}

export function ProductsCell({ className, align, children }: ProductsCellProps) {
    return (
        <Cell role="cell" align={align} className={classNames(CELL_CLASS, className)}>
            {children}
        </Cell>
    )
}

const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

function useRowFlip(body: React.RefObject<HTMLDivElement>, sort: TableSort | null) {
    const before = React.useRef<Map<Element, number> | null>(null)

    React.useLayoutEffect(() => {
        const tops = before.current
        before.current = null
        const element = body.current
        if (!tops || !element || prefersReducedMotion()) {
            return
        }
        const moved: HTMLElement[] = []
        Array.from(element.children).forEach((row) => {
            const top = tops.get(row)
            if (top === undefined) {
                return
            }
            const delta = top - row.getBoundingClientRect().top
            if (delta !== 0) {
                const rowElement = row as HTMLElement
                rowElement.classList.add(FLIP_HOLD_CLASS)
                rowElement.style.transform = `translateY(${delta}px)`
                moved.push(rowElement)
            }
        })
        if (moved.length === 0) {
            return
        }
        element.getBoundingClientRect()
        moved.forEach((row) => {
            row.classList.remove(FLIP_HOLD_CLASS)
            row.style.removeProperty('transform')
        })
    }, [sort])

    return () => {
        const element = body.current
        if (!element) {
            return
        }
        const tops = new Map<Element, number>()
        Array.from(element.children).forEach((row) => tops.set(row, row.getBoundingClientRect().top))
        before.current = tops
    }
}

const skeletonCell = (index: number, dataColumns: number): React.ReactNode => {
    if (index === 0) {
        return (
            <span className="capacity-table-skeleton-stack">
                <SkeletonBar width="9rem" height="0.75rem" className="capacity-table-skeleton-bar" />
                <SkeletonBar width="14rem" height="0.75rem" className="capacity-table-skeleton-bar" />
            </span>
        )
    }
    if (index === dataColumns) {
        return (
            <SkeletonBar
                width="1.75rem"
                height="1.75rem"
                className="capacity-table-skeleton-bar capacity-table-skeleton-square"
            />
        )
    }
    return <SkeletonBar width="3rem" height="0.75rem" className="capacity-table-skeleton-bar" />
}

interface ProductsHeaderCellProps {
    column: ProductsColumn
    sort: TableSort | null
    tableSorting: { prop: string; direction: 'asc' | 'desc' }
    onSort: (key: string) => void
}

function ProductsHeaderCell({ column, sort, tableSorting, onSort }: ProductsHeaderCellProps) {
    const sortable = !!column.sortable
    const ariaSort = sortable ? (sort?.key === column.key ? sort.direction : 'none') : undefined
    const onKeyDown = (event: React.KeyboardEvent) => {
        if (sortable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            onSort(column.key)
        }
    }
    return (
        <TableHeader
            role="columnheader"
            aria-sort={ariaSort}
            align={column.align}
            className="capacity-table-th"
            nonInteractive={!sortable}
            sortKey={sortable ? column.key : undefined}
            sorting={tableSorting}
            changeSort={() => onSort(column.key)}
            tabIndex={sortable ? 0 : undefined}
            onKeyDown={onKeyDown}
        >
            {column.headerTooltip ? (
                <TooltipWrapper
                    content={column.headerTooltip}
                    className="capacity-tip capacity-header-tip"
                    revealOnFocus
                >
                    <span>{column.header}</span>
                </TooltipWrapper>
            ) : (
                column.header
            )}
        </TableHeader>
    )
}

interface ProductsTableProps {
    columns: ProductsColumn[]
    sort: TableSort | null
    onSort: (key: string) => void
    loading: boolean
    error: boolean
    onRetry: () => void
    empty: boolean
    filteredEmpty: React.ReactNode
    rows: React.ReactNode[]
    children?: React.ReactNode
}

export function ProductsTable({
    columns,
    sort,
    onSort,
    loading,
    error,
    onRetry,
    empty,
    filteredEmpty,
    rows,
    children,
}: ProductsTableProps) {
    const { t } = useTranslation()
    const bodyRef = React.useRef<HTMLDivElement>(null)
    const measureBeforeSort = useRowFlip(bodyRef, sort)
    const columnWidths = [...columns.map((column) => column.width ?? null), ACTIONS_COLUMN_WIDTH]
    const tableSorting = {
        prop: sort?.key ?? '',
        direction: sort?.direction === 'descending' ? ('desc' as const) : ('asc' as const),
    }
    const tableOpen = !loading && !error && rows.length > 0
    const filteredEmptyOpen = !loading && !error && !empty && rows.length === 0

    const sortClick = (key: string) => {
        measureBeforeSort()
        onSort(key)
    }

    return (
        <>
            <div className="capacity-table-card">
                <Reveal open={loading}>
                    <InlineDataTable
                        bordered
                        role="table"
                        aria-label={t('capacity.products.loadingProducts')}
                        className="capacity-table"
                        columnWidths={columnWidths}
                    >
                        <div role="rowgroup">
                            {SKELETON_ROWS.map((row) => (
                                <ProductsRow key={row} className="capacity-table-row-skeleton">
                                    {Array.from({ length: columns.length + 1 }, (_, index) => (
                                        <ProductsCell key={index}>{skeletonCell(index, columns.length)}</ProductsCell>
                                    ))}
                                </ProductsRow>
                            ))}
                        </div>
                    </InlineDataTable>
                </Reveal>
                <Reveal open={error}>
                    <p className="capacity-table-note">
                        {t('capacity.products.error')}{' '}
                        <button type="button" className="capacity-text-action" onClick={onRetry}>
                            {t('capacity.products.retry')}
                        </button>
                    </p>
                </Reveal>
                <Reveal open={!loading && !error && empty}>
                    <p className="capacity-table-note">{t('capacity.products.empty')}</p>
                </Reveal>
                <Reveal open={filteredEmptyOpen}>{filteredEmpty}</Reveal>
                <Reveal open={tableOpen}>
                    <InlineDataTable bordered role="table" className="capacity-table" columnWidths={columnWidths}>
                        <HeaderRow role="row" className="capacity-table-head">
                            {columns.map((column) => (
                                <ProductsHeaderCell
                                    key={column.key}
                                    column={column}
                                    sort={sort}
                                    tableSorting={tableSorting}
                                    onSort={sortClick}
                                />
                            ))}
                            <TableHeader nonInteractive role="columnheader" className="capacity-table-th">
                                <span className="capacity-visually-hidden">{t('capacity.products.actions')}</span>
                            </TableHeader>
                        </HeaderRow>
                        <div role="rowgroup" ref={bodyRef}>
                            {rows}
                        </div>
                    </InlineDataTable>
                </Reveal>
            </div>
            {children}
        </>
    )
}
